import { useEffect, useRef, useState } from 'react';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { resolveBatches } from './recipient-field';
import type { ResolvedShareRecipientSummary } from '../pages/api/share-recipients';

/**
 * Which of these addresses already have a Symphony account.
 *
 * <p>Answers a map from address to `true` / `false`, and an address is ABSENT from the map when the
 * lookup did not answer. That third state is the whole point: the service's resolver refuses to
 * guess, because a stranger badged "on Symphony" is exactly the assurance somebody acts on before
 * handing over a document. A caller must render nothing for absent.
 *
 * <p>Answers accumulate across queries and are never dropped: the addresses on screen change with
 * every keystroke, and re-asking about somebody already resolved would spend the caller's rate limit
 * on an answer we hold. Nothing invalidates them because the fact barely moves — an account is not
 * un-created — and a stale `true` for the length of one dialog is not a claim worth a round trip.
 *
 * <p>NOT a React Query hook, deliberately: this is a many-keys-to-one-request read whose keys change
 * on every keystroke, which is the one shape `queryKey` serves badly — each distinct set would be its
 * own cache entry, and the shared answers would be invisible between them.
 */
export function useShareMembership(emails: string[]): Map<string, boolean> {
  const [known, setKnown] = useState<Map<string, boolean>>(new Map());
  // Asked-about, answered or not. Without it an address the service could not answer for is re-asked
  // on every render — the rate limit spent hardest on exactly the addresses it cannot help with.
  const asked = useRef(new Set<string>());

  const wanted = emails.map((e) => e.trim().toLowerCase()).filter(Boolean);
  // Keyed on ALL the addresses in play, not the unasked subset: each answer re-renders, and a key
  // on the unasked set would change and cancel the run still working through the later chunks.
  const key = Array.from(new Set(wanted)).sort().join(',');

  useEffect(() => {
    const batch = Array.from(new Set(wanted.filter((e) => !asked.current.has(e))));
    if (batch.length === 0) return undefined;
    batch.forEach((e) => asked.current.add(e));
    let cancelled = false;

    void (async () => {
      // The cap belongs to the service; this only keeps a long list from being refused wholesale.
      for (const chunk of resolveBatches(batch)) {
        try {
          const res = await apiFetch(`${API_BASE}/api/share-recipients`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ emails: chunk }),
          });
          // A refusal — 429 above all — is silence, not a failure to report. The field renders
          // nothing, the share is unaffected, and the server-side stranger gate still runs.
          // A cancelled run STOPS rather than firing its remaining chunks at a dialog nobody is
          // looking at any more. `!res.ok` continues, because the next chunk is about different
          // people and one refused batch says nothing about them.
          if (cancelled) return;
          if (!res.ok) continue;
          const data = (await res.json()) as { recipients?: ResolvedShareRecipientSummary[] };
          if (cancelled) return;
          setKnown((previous) => {
            const next = new Map(previous);
            for (const r of data.recipients ?? []) {
              // ONLY an explicit boolean. Absent stays absent — the map's missing key IS the third state.
              if (typeof r.hasSymphonyAccount === 'boolean') next.set(r.email.trim().toLowerCase(), r.hasSymphonyAccount);
            }
            return next;
          });
        } catch {
          // Same as a refusal: we did not find out, and saying so is the honest render.
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // `key` is every address in play. The `asked` ref is what stops a re-ask; the key only has to
    // change when the SET of interest changes, and must NOT change merely because an answer landed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return known;
}
